import {
  BreakpointsEnum,
  CaesarsButton,
  isBreakpointDown,
  isBreakpointUp,
  theme,
  useBreakpoint,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {Box, Heading, View, useBreakpointValue} from 'native-base';
import React, {useState} from 'react';
import {Dimensions, LayoutChangeEvent} from 'react-native';
import {OFFERS_GROUPING} from '../OfferGroup';

import ConnectedOfferCard from 'containers/OfferCard';
import {Offer} from 'models/Offer';

import {getMarginRight} from '../OfferGroup';

export type OfferCardListProps = {
  /**
   * Section title of the row of offers
   */
  sectionTitle: string;
  /**
   * List of offers to display in this row
   */
  offers: Offer[];
  /**
   * Calls the callback prop when see more is pressed
   */
  handleSeeMorePressed: () => void;
  isListView: boolean;
  testIdSuffix?: string;
  setSelectedGrouping: () => void;
  groupingKey?: OFFERS_GROUPING;
};

const OfferCardList: React.FC<OfferCardListProps> = ({
  sectionTitle,
  offers,
  handleSeeMorePressed,
  testIdSuffix,
  setSelectedGrouping,
  isListView = false,
  groupingKey,
}) => {
  const breakpoint = useBreakpoint();
  const isNano = isBreakpointDown(breakpoint, BreakpointsEnum.NANO);
  const isMicro = isBreakpointDown(breakpoint, BreakpointsEnum.MICRO);
  const isXsmall = isBreakpointDown(breakpoint, BreakpointsEnum.XS);
  const isSmall = isBreakpointDown(breakpoint, BreakpointsEnum.SM);
  const isDesktop = isBreakpointUp(breakpoint, BreakpointsEnum.XL);
  const isLargeDesktop = isBreakpointUp(breakpoint, BreakpointsEnum.XXL);
  const isTwoRowLayout = !isNano && isXsmall;
  const listView = isNano || (isListView && !isDesktop);
  const [containerWidth, setContainerWidth] = useState(0);

  const maxCardNumber = () => {
    if (isLargeDesktop) {
      return 4;
    }
    if (isDesktop) {
      return 3;
    }
    if (isXsmall) {
      return 4;
    }
    if (isSmall) {
      return 3;
    }
    return 4;
  };

  const cardWidth = useBreakpointValue({
    base: 164, // 0 - 479
    sm: 176, // 480 - 767
    md: 176, // 768 - 959
    lg: 176, // 960 - 1169
    xl: 238, // 1170 - 1439
    '2xl': 238, // 1440+
  });

  const hasMoreOffers = offers.length > maxCardNumber();
  const maxRowNumber = listView ? 1 : isXsmall ? 2 : maxCardNumber();
  const {marginRight, numCards} = getMarginRight({
    cardWidth,
    maxCardNumber: maxRowNumber,
    containerWidth,
    isTwoRowLayout,
  });

  const sliceOffers = (offersArray: Offer[]): Offer[] => {
    if (hasMoreOffers) {
      return offers.slice(0, maxCardNumber());
    }
    return offersArray;
  };

  const renderItem = (item: Offer, i: number) => {
    const isLastCard = (i + 1) % numCards === 0;

    const calcMarginBottom = () => {
      if (listView || (isMicro && i <= 2) || isTwoRowLayout) {
        return '16px';
      } else if (isXsmall && i <= 2) {
        return '32px';
      }
      return 0;
    };

    const calcMarginRight = () => {
      if (isLastCard || listView) {
        return 0;
      }
      if (isTwoRowLayout) {
        return '16px';
      }
      return `${marginRight}px`;
    };

    const calcFlexBasis = () => {
      if (isTwoRowLayout && Dimensions.get('window').width <= 432) {
        return '47%';
      }
      if (isTwoRowLayout) {
        return '48%';
      }
      return undefined;
    };

    return (
      <Box
        key={`OfferCard-${item.id}`}
        flexBasis={calcFlexBasis()}
        mr={calcMarginRight()}
        mb={calcMarginBottom()}>
        <ConnectedOfferCard
          cardIndex={i}
          testID={`offer-card-${testIdSuffix}`}
          offer={item}
          setSelectedGrouping={setSelectedGrouping}
          groupingKey={groupingKey}
        />
      </Box>
    );
  };

  const handleLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  return (
    <Box
      borderTopWidth={isSmall ? '1px' : 0}
      testID={`offer-group-${testIdSuffix}`}
      flex={1}
      width="100%"
      borderColor="lightgrey"
      mb="32px"
      pt="32px">
      <Box
        testID="offer-group-header"
        marginBottom="32px"
        justifyContent="space-between"
        alignItems="center"
        flex={1}
        width="100%"
        flexDirection="row">
        <Heading
          testID="offer-group-section-title"
          fontFamily="heading"
          fontSize={4}
          color="tuna"
          fontWeight={700}>
          {`${sectionTitle} (${offers.length})`}
        </Heading>
        {hasMoreOffers && !isSmall && (
          <CaesarsButton
            accessibilityLabel={`See more ${sectionTitle}`}
            accessibilityRole="button"
            testID="offer-group-see-more-button"
            onPress={handleSeeMorePressed}
            title="See More >"
          />
        )}
      </Box>
      <Box
        onLayout={handleLayout}
        overflow={'visible'}
        flex={1}
        width="100%"
        flexDirection={listView ? 'column' : 'row'}
        justifyContent={
          listView ? 'center' : isTwoRowLayout ? 'space-between' : 'flex-start'
        }
        flexWrap="wrap">
        {sliceOffers(offers).map(renderItem)}
      </Box>
      <Box>
        {hasMoreOffers && isSmall && (
          <View mt="24px">
            <CaesarsButton
              isFullWidth
              accessibilityLabel={`See more ${sectionTitle}`}
              accessibilityRole="button"
              testID="offer-group-see-more-button"
              onPress={handleSeeMorePressed}
              title="See More >"
            />
          </View>
        )}
      </Box>
    </Box>
  );
};

export default OfferCardList;
